// AUPRÉA — Réseau (Mon équipe consultant + Candidatures admin + Vision réseau + Fiche recrue)
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { C, Btn, Input, Select, Textarea, Spinner, Alert, Badge, fmtEur } from "./shared";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const authHdrs = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });

// ── Constants et helpers ────────────────────────────────────────────────────
const STATUT_COLORS = {
  en_attente_validation: { label: "En attente", bg: "#FEF3C7", fg: "#B45309" },
  en_integration:        { label: "En intégration", bg: "#E6F1FB", fg: "#185FA5" },
  actif:                 { label: "Actif", bg: "#DBEEE9", fg: "#1F7A6C" },
  inactif:               { label: "Inactif", bg: "#F1EFEC", fg: "#5F5E5A" },
  suspendu:              { label: "Suspendu", bg: "#FEE2E2", fg: "#991B1B" },
};
const StatutBadge = ({ statut }) => {
  const m = STATUT_COLORS[statut] || { label: statut, bg: C.gray2, fg: C.mid };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600, background: m.bg, color: m.fg }}>
    <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.fg }} /> {m.label}
  </span>;
};
const RoleBadge = ({ role }) => {
  const map = { conseiller: "Consultant", partenaire_recruteur: "Partenaire Recruteur", admin: "Admin" };
  const col = { conseiller: C.blue, partenaire_recruteur: C.gold, admin: C.purp };
  return <span style={{ padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: col[role] + "22", color: col[role] }}>{map[role] || role}</span>;
};

// ═══════════════════════════════════════════════════════════════════════════
// MON ÉQUIPE (consultant)
// ═══════════════════════════════════════════════════════════════════════════
export const MonEquipe = () => {
  const [data, setData] = useState(null);
  const [constants, setConstants] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const load = async () => {
    const [eq, cst, inv] = await Promise.all([
      axios.get(`${API}/mon-equipe`, authHdrs()).then(r => r.data),
      axios.get(`${API}/reseau/constants`).then(r => r.data),
      axios.get(`${API}/consultants/invitation-personnelle`, authHdrs()).then(r => r.data).catch(() => null),
    ]);
    setData(eq); setConstants(cst); setInvitation(inv);
  };
  useEffect(() => { load(); }, []);
  if (!data || !constants) return <Spinner />;

  const k = data.kpi;
  return (
    <div data-testid="mon-equipe-page">
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        {[
          ["Niveau 1", k.n1, C.gold],
          ["Niveau 2", k.n2, C.teal],
          ["Niveau 3", k.n3, C.blue],
          ["Total équipe", k.total, C.navy],
          ["Actifs", k.actifs, C.green],
          ["Nouveaux ce mois", k.nouveaux_mois, C.purp],
          ["Commission équipe", `${fmtEur(k.commission_equipe)}`, C.golddm],
        ].map(([lbl, val, col]) => (
          <div key={lbl} data-testid={`eq-kpi-${lbl.toLowerCase().replace(/ /g,'-')}`} style={{ background: "#fff", padding: 12, border: `.5px solid ${C.border}`, borderRadius: 10, borderTop: `3px solid ${col}` }}>
            <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700 }}>{lbl}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: col }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <Btn variant="gold" onClick={() => setShowAdd(true)} data-testid="btn-ajouter-conseiller">+ Ajouter un consultant</Btn>
        <Btn onClick={() => setShowInvite(true)} data-testid="btn-mon-lien-invitation">🔗 Mon lien d'invitation</Btn>
      </div>

      {/* Niveaux N1, N2, N3 */}
      {[1, 2, 3].map(lvl => (
        <div key={lvl} style={{ marginBottom: 24 }} data-testid={`level-${lvl}`}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>
            Niveau {lvl} · {data.levels[lvl].length} membre{data.levels[lvl].length > 1 ? "s" : ""}
          </div>
          {data.levels[lvl].length === 0 ? (
            <div style={{ background: "#fff", border: `.5px dashed ${C.border}`, borderRadius: 10, padding: 20, textAlign: "center", color: C.muted, fontSize: 12 }}>
              Aucun filleul à ce niveau
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
              {data.levels[lvl].map(m => (
                <MemberCard key={m.id} member={m} />
              ))}
            </div>
          )}
        </div>
      ))}

      {showAdd && <AddConseillerModal constants={constants} onClose={() => setShowAdd(false)} onDone={load} />}
      {showInvite && invitation && <InvitationModal invitation={invitation} onClose={() => setShowInvite(false)} />}
    </div>
  );
};

const MemberCard = ({ member: m }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 12 }} data-testid={`member-${m.id}`}>
    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", background: (m.color || C.mid) + "22", color: m.color || C.mid, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11 }}>{m.initials}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: C.navy }}>{m.prenom} {m.nom}</div>
        <div style={{ fontSize: 10, color: C.muted }}>{m.zone}{m.zone_secondaire ? ` + ${m.zone_secondaire}` : ""}</div>
      </div>
      {m.telephone && <a href={`https://wa.me/${m.telephone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" style={{ background: "#25D366", color: "#fff", padding: "3px 7px", borderRadius: 5, fontSize: 10, textDecoration: "none", fontWeight: 600 }}>💬</a>}
    </div>
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
      <StatutBadge statut={m.statut} />
      <RoleBadge role={m.role_user} />
      {m.cotisation_a_jour !== undefined && <span data-testid={`equipe-cot-${m.id}`} style={{ padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: m.cotisation_a_jour ? "#E3F4EA" : "#FFF4DC", color: m.cotisation_a_jour ? "#1E7B45" : "#B07A12" }}>{m.cotisation_a_jour ? "Cotisation à jour ✓" : "Cotisation en attente"}</span>}
      {m.palier_mois && <span style={{ padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: C.goldlt, color: C.golddm }}>{m.palier_mois}</span>}
      {m.inactif_30j && <span style={{ padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: "#FEE2E2", color: "#991B1B" }}>⚠ Inactif 30j</span>}
    </div>
    <div style={{ fontSize: 11, color: C.mid }}>
      {m.ventes_mois} vente{m.ventes_mois > 1 ? "s" : ""} ce mois
      {m.statut === "en_integration" && <span style={{ color: C.gold }}> · Étape {m.etape_integration + 1}/7</span>}
    </div>
  </div>
);

const AddConseillerModal = ({ constants, onClose, onDone }) => {
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    zone: constants.zones[0], formule_cotisation: "Conseiller", message_perso: "",
  });
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.post(`${API}/consultants/inviter-recrue`, form, authHdrs());
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    }
    setLoading(false);
  };

  if (result) {
    return (
      <ModalShell title="Recrue inscrite ✓" onClose={() => { onClose(); onDone(); }}>
        <div data-testid="invitation-email-status" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
          {result.email_status === "failed"
            ? <>L'e-mail vers <strong>{form.email}</strong> n'a pas pu partir : partagez le lien via WhatsApp ci-dessous.</>
            : result.email_status === "skipped"
            ? <>Invitation créée (mode test : aucun e-mail envoyé à <strong>{form.email}</strong>). Partagez le lien ci-dessous.</>
            : <>La demande de <strong>{form.prenom}</strong> part en validation AUPRÉA. Suivez son intégration dans « Mes recrues ».</>}
        </div>
        {result.wa_link && (
          <a href={result.wa_link} target="_blank" rel="noopener noreferrer"
             data-testid="wa-invite-link"
             style={{ display: "block", background: "#25D366", color: "#fff", textDecoration: "none", padding: "10px 16px", borderRadius: 8, textAlign: "center", fontWeight: 600 }}>
            💬 Envoyer aussi par WhatsApp
          </a>
        )}
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Ajouter un consultant" onClose={onClose}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Input placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="add-prenom" />
          <Input placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="add-nom" />
        </div>
        <Input placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-email" />
        <Input placeholder="Téléphone (ex +590 690...)" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="add-tel" />
        <Select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="add-zone">
          {constants.zones.map(z => <option key={z} value={z}>{z}</option>)}
        </Select>
        <Select value={form.formule_cotisation} onChange={(e) => setForm({ ...form, formule_cotisation: e.target.value })} data-testid="add-formule">
          {constants.formules.map(f => <option key={f} value={f}>Formule {f.replace("Conseiller", "Consultant")}</option>)}
        </Select>
        <Textarea placeholder="Message personnel (optionnel)" value={form.message_perso} onChange={(e) => setForm({ ...form, message_perso: e.target.value })} rows={3} />
        <div style={{ fontSize: 11, color: C.mid, background: C.navylt + "66", padding: 10, borderRadius: 6 }}>
          ℹ Parrain : <strong>vous-même</strong> (non modifiable). Rôle : <strong>Consultant</strong> (non modifiable).
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn onClick={onClose}>Annuler</Btn>
          <Btn variant="gold" onClick={submit} disabled={loading} data-testid="add-submit">
            {loading ? "..." : "Inscrire la recrue"}
          </Btn>
        </div>
      </div>
    </ModalShell>
  );
};

const InvitationModal = ({ invitation, onClose }) => {
  const [copied, setCopied] = useState(false);
  const link = invitation.lien_absolu_hint || invitation.lien;
  const absoluteLink = `${window.location.origin}${invitation.lien}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(absoluteLink)}&color=0A2A46`;
  const copy = async () => {
    await navigator.clipboard.writeText(absoluteLink);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  const wa = `https://wa.me/?text=${encodeURIComponent("Rejoignez le réseau AUPRÉA : " + absoluteLink)}`;
  const mail = `mailto:?subject=Rejoignez%20le%20r%C3%A9seau%20AUPR%C3%89A&body=${encodeURIComponent(absoluteLink)}`;
  return (
    <ModalShell title="Mon lien d'invitation personnel" onClose={onClose}>
      <div style={{ textAlign: "center", padding: 10 }}>
        <img src={qrSrc} alt="QR" style={{ borderRadius: 10, border: `1px solid ${C.border}` }} data-testid="qr-code" />
        <div style={{ fontSize: 12, color: C.mid, marginTop: 10 }}>
          Code personnel : <strong style={{ fontFamily: "monospace", color: C.navy }}>{invitation.code}</strong>
        </div>
        <div style={{ fontSize: 13, color: C.navy, fontWeight: 500, marginTop: 6, wordBreak: "break-all", background: C.gray2, padding: 10, borderRadius: 6 }} data-testid="invitation-link">
          {absoluteLink}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
        <Btn onClick={copy} data-testid="copy-link">{copied ? "✓ Copié" : "📋 Copier"}</Btn>
        <a href={wa} target="_blank" rel="noopener noreferrer" style={{ background: "#25D366", color: "#fff", padding: "7px 14px", borderRadius: 6, textDecoration: "none", fontWeight: 600, fontSize: 12 }}>💬 WhatsApp</a>
        <a href={mail} style={{ background: C.navy, color: "#fff", padding: "7px 14px", borderRadius: 6, textDecoration: "none", fontWeight: 600, fontSize: 12 }}>✉ E-mail</a>
      </div>
    </ModalShell>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CANDIDATURES ADMIN (2 listes : Invitations envoyées + Candidatures à traiter)
// ═══════════════════════════════════════════════════════════════════════════
export const CandidaturesAdmin = () => {
  const [invitations, setInvitations] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [counts, setCounts] = useState(null);
  const [emailsLog, setEmailsLog] = useState([]);
  const [openCand, setOpenCand] = useState(null);
  const [tab, setTab] = useState("candidatures"); // candidatures | invitations | emails
  const [loading, setLoading] = useState(true);
  const [journal, setJournal] = useState([]);
  const [sansAcces, setSansAcces] = useState([]);
  const [sending, setSending] = useState(false);

  const envoyerAcces = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/conseillers/acces-manquants/envoyer`, {}, authHdrs());
      const ko = data.resultats.filter(x => x.status !== "sent" && x.status !== "deja_actif");
      alert(`${data.envoyes} e-mail(s) d'accès envoyé(s)${ko.length ? ` · ${ko.length} non envoyé(s), voir Journal e-mails` : " ✓"}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
    setSending(false);
  };

  const load = async () => {
    setLoading(true);
    const [inv, cand, cnt, emails, journal, sans] = await Promise.all([
      axios.get(`${API}/admin/candidatures?statut=invitee`, authHdrs()).then(r => r.data),
      axios.get(`${API}/admin/candidatures?statut=en_attente`, authHdrs()).then(r => r.data),
      axios.get(`${API}/admin/candidatures/counts`, authHdrs()).then(r => r.data),
      axios.get(`${API}/admin/emails-log?status=failed`, authHdrs()).then(r => r.data).catch(() => []),
      axios.get(`${API}/admin/emails-log`, authHdrs()).then(r => r.data).catch(() => []),
      axios.get(`${API}/admin/conseillers/acces-manquants`, authHdrs()).then(r => r.data).catch(() => []),
    ]);
    setInvitations(inv); setCandidatures(cand); setCounts(cnt); setEmailsLog(emails); setJournal(journal); setSansAcces(sans);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const action = async (cid, action, motif, type_contrat) => {
    try {
      await axios.post(`${API}/admin/candidatures/${cid}/action`, { action, motif, type_contrat }, authHdrs());
      setOpenCand(null); load();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };

  const relancer = async (cid) => {
    try {
      const { data } = await axios.post(`${API}/admin/invitations/${cid}/relancer`, {}, authHdrs());
      alert(data.email_status === "sent" ? "Invitation relancée — e-mail envoyé ✓" : `Invitation prolongée (e-mail : ${data.email_status})`); load();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };

  const renvoyerEmail = async (eid) => {
    try {
      const { data } = await axios.post(`${API}/admin/emails-log/${eid}/renvoyer`, {}, authHdrs());
      alert(data.ok ? "E-mail renvoyé ✓" : `Échec du renvoi : ${data.error || data.status}`); load();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <Spinner />;

  const daysSince = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div data-testid="candidatures-admin">
      {sansAcces.length > 0 && (
        <div data-testid="banner-acces-manquants" style={{ background: C.amber + "14", border: `.5px solid ${C.amber}`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>{sansAcces.length} consultant(s) validé(s) n'ont pas encore activé leur accès</div>
              <div style={{ fontSize: 11, color: C.mid, marginTop: 4 }}>
                {sansAcces.map(x => `${x.prenom} ${x.nom}`).join(" · ")}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Chacun recevra un nouveau lien (valable 48 h) pour créer son mot de passe, et le montant de sa cotisation.</div>
            </div>
            <Btn variant="gold" small onClick={envoyerAcces} disabled={sending} data-testid="btn-envoyer-acces">
              {sending ? "Envoi…" : `✉ Envoyer les accès (${sansAcces.length})`}
            </Btn>
          </div>
        </div>
      )}

      {/* Compteurs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 18 }}>
        {[
          ["Invitations envoyées", counts?.invitations_envoyees || 0, C.gold, "invitations"],
          ["Candidatures à traiter", counts?.candidatures_a_traiter || 0, C.red, "candidatures"],
          ["E-mails en échec", counts?.emails_echec || 0, C.amber, "emails"],
          ["Journal e-mails", journal.length, C.navy, "journal"],
        ].map(([lbl, val, col, tbKey]) => (
          <button key={lbl} onClick={() => setTab(tbKey)}
                  data-testid={`cnt-${tbKey}`}
                  style={{ background: tab === tbKey ? col + "22" : "#fff", padding: 14, border: `.5px solid ${tab === tbKey ? col : C.border}`, borderRadius: 10, borderTop: `3px solid ${col}`, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700 }}>{lbl}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: col }}>{val}</div>
          </button>
        ))}
      </div>

      {/* Onglet INVITATIONS ENVOYÉES */}
      {tab === "invitations" && (
        <div data-testid="list-invitations">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
            Invitations envoyées (en attente de la candidature)
          </div>
          {!invitations.length ? (
            <div style={{ background: "#fff", border: `.5px dashed ${C.border}`, padding: 40, textAlign: "center", color: C.muted, borderRadius: 10 }}>Aucune invitation en attente</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {invitations.map(c => (
                <div key={c.id} data-testid={`inv-${c.id}`} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${C.gold}`, borderRadius: 10, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: C.navy }}>{c.prenom} {c.nom}</div>
                      <div style={{ fontSize: 12, color: C.mid }}>{c.email} · {c.telephone || "—"}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                        Parrain : <strong>{(c.parrain_nom || "Direct").split(" ").filter((m, n, a) => n === 0 || m !== a[n - 1]).join(" ")}</strong> · Zone : {c.zone} · Formule {(c.formule_cotisation || "").replace("Conseiller", "Consultant")}
                        · Envoyée le {(c.created_at || "").slice(0, 10)} ({daysSince(c.created_at)} jour{daysSince(c.created_at) > 1 ? "s" : ""})
                        {c.relances_count > 0 && <span style={{ color: C.gold }}> · {c.relances_count} relance{c.relances_count > 1 ? "s" : ""}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn small onClick={() => relancer(c.id)} data-testid={`inv-relancer-${c.id}`}>↻ Relancer</Btn>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onglet CANDIDATURES À TRAITER */}
      {tab === "candidatures" && (
        <div data-testid="list-candidatures">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
            Candidatures complétées — en attente de validation
          </div>
          {!candidatures.length ? (
            <div style={{ background: "#fff", border: `.5px dashed ${C.border}`, padding: 40, textAlign: "center", color: C.muted, borderRadius: 10 }}>Aucune candidature à traiter</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {candidatures.map(c => (
                <div key={c.id} data-testid={`cand-${c.id}`} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${C.red}`, borderRadius: 10, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: C.navy }}>{c.prenom} {c.nom}</div>
                      <div style={{ fontSize: 12, color: C.mid }}>{c.email} · {c.telephone}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                        Zone : {c.zone} · Formule {(c.formule_cotisation || "").replace("Conseiller", "Consultant")} · Parrain : <strong>{(c.parrain_nom || "Direct").split(" ").filter((m, n, a) => n === 0 || m !== a[n - 1]).join(" ")}</strong>
                        · Reçue le {(c.submitted_at || c.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Btn variant="gold" small onClick={() => setOpenCand({ ...c, pending_action: "valider" })} data-testid={`cand-valider-${c.id}`}>✓ Valider</Btn>
                      <Btn small onClick={() => setOpenCand({ ...c, pending_action: "attente" })} data-testid={`cand-attente-${c.id}`}>⏸ Attente</Btn>
                      <Btn small onClick={() => setOpenCand({ ...c, pending_action: "refuser" })} data-testid={`cand-refuser-${c.id}`}>✗ Refuser</Btn>
                    </div>
                  </div>
                  {c.experience && <div style={{ fontSize: 11, color: C.mid, marginTop: 8, fontStyle: "italic" }}>« {c.experience} »</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onglet E-MAILS EN ÉCHEC */}
      {tab === "emails" && (
        <div data-testid="list-emails-failed">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
            E-mails en échec (à renvoyer)
          </div>
          {!emailsLog.length ? (
            <div style={{ background: "#fff", border: `.5px dashed ${C.border}`, padding: 40, textAlign: "center", color: C.muted, borderRadius: 10 }}>Aucun échec d'envoi 🎉</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {emailsLog.map(e => (
                <div key={e.id} data-testid={`email-fail-${e.id}`} style={{ background: "#fff", border: `.5px solid ${C.red}`, borderRadius: 8, padding: 10, display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>→ {e.to}</div>
                    <div style={{ fontSize: 11, color: C.mid }}>{e.subject}</div>
                    {e.error && <div style={{ fontSize: 10, color: C.red }}>Erreur : {e.error}</div>}
                  </div>
                  <Btn small onClick={() => renvoyerEmail(e.id)} data-testid={`email-renvoyer-${e.id}`}>↻ Renvoyer</Btn>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "journal" && <EmailJournal items={journal} />}

      {openCand && <MotifModal candidature={openCand} onClose={() => setOpenCand(null)} onConfirm={(motif, tc) => action(openCand.id, openCand.pending_action, motif, tc)} />}
    </div>
  );
};

const EMAIL_STATUS = { sent: ["Envoyé", C.green], failed: ["Échec", C.red], skipped: ["Non envoyé", C.muted] };

const EmailJournal = ({ items }) => (
  <div data-testid="list-emails-journal">
    <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
      200 derniers e-mails · expéditeur « AUPRÉA » · réponses vers contact@auprea.com
    </div>
    {!items.length ? (
      <div style={{ background: "#fff", border: `.5px dashed ${C.border}`, padding: 40, textAlign: "center", color: C.muted, borderRadius: 10 }}>Aucun e-mail envoyé pour l'instant</div>
    ) : (
      <div style={{ display: "grid", gap: 6 }}>
        {items.map(e => {
          const [lbl, col] = EMAIL_STATUS[e.status] || ["Ancien (simulé)", C.muted];
          return (
            <div key={e.id} data-testid={`email-journal-${e.id}`} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", display: "flex", gap: 12, alignItems: "center" }}>
              <span data-testid={`email-status-${e.id}`} style={{ fontSize: 10, fontWeight: 700, color: col, border: `.5px solid ${col}`, borderRadius: 20, padding: "2px 8px", whiteSpace: "nowrap" }}>{lbl}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.subject}</div>
                <div style={{ fontSize: 11, color: C.mid }}>→ {e.to} · {(e.created_at || "").slice(0, 16).replace("T", " ")}{e.error ? ` · ${e.error}` : ""}</div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const TYPES_CONTRAT = ["Consultant (apporteur d'affaires)", "Coordinateur régional", "Partenaire recruteur"];
const MotifModal = ({ candidature, onClose, onConfirm }) => {
  const [motif, setMotif] = useState("");
  const [tc, setTc] = useState(TYPES_CONTRAT[0]);
  const a = candidature.pending_action;
  return (
    <ModalShell title={a === "valider" ? `Valider ${candidature.prenom} ${candidature.nom}` : a === "attente" ? "Mettre en attente" : "Refuser la candidature"} onClose={onClose}>
      {a === "valider" ? (
        <div>
          <div style={{ fontSize: 12, color: C.mid, marginBottom: 6 }}>Type de contrat</div>
          <Select value={tc} onChange={(e) => setTc(e.target.value)} data-testid="type-contrat-select">
            {TYPES_CONTRAT.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
          <div style={{ fontSize: 11, color: C.mid, marginTop: 8 }}>Un e-mail part avec un lien personnel (48 h) pour créer le mot de passe.</div>
        </div>
      ) : <Textarea placeholder={a === "refuser" ? "Motif du refus (obligatoire, envoyé à la recrue)" : "Motif / message (sera envoyé à la recrue)"} value={motif} onChange={(e) => setMotif(e.target.value)} rows={4} data-testid="motif-input" />}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
        <Btn onClick={onClose}>Annuler</Btn>
        <Btn variant="gold" disabled={a === "refuser" && !motif.trim()} onClick={() => onConfirm(motif, tc)} data-testid="motif-confirm">Confirmer</Btn>
      </div>
    </ModalShell>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VISION RÉSEAU (admin)
// ═══════════════════════════════════════════════════════════════════════════
export const ReseauAdminV2 = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/reseau/overview`, authHdrs()).then(r => setData(r.data)).catch(console.error);
  }, []);
  const downloadCsv = async () => {
    const res = await fetch(`${API}/admin/reseau/export-csv`, authHdrs());
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "reseau_auprea.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  if (!data) return <Spinner />;

  return (
    <div data-testid="reseau-admin-page">
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        {[
          ["Total", data.total, C.navy],
          ["Actifs", data.actifs, C.green],
          ["Taux activation", `${data.taux_activation}%`, C.teal],
          ["Inactifs 30j", data.inactifs_30j, C.amber],
          ["Candidatures", data.candidatures_en_attente, C.gold],
          ["Contrats pending", data.contrats_pending, C.purp],
          ["Cotisations pending", data.cotisations_pending, C.red],
        ].map(([lbl, val, col]) => (
          <div key={lbl} data-testid={`r-kpi-${lbl.toLowerCase().replace(/ /g, '-')}`}
               style={{ background: "#fff", padding: 12, border: `.5px solid ${C.border}`, borderRadius: 10, borderTop: `3px solid ${col}` }}>
            <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700 }}>{lbl}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: col }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Export */}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <Btn variant="gold" onClick={downloadCsv} data-testid="btn-reseau-csv">↓ Export CSV réseau</Btn>
      </div>

      {/* Évolution mensuelle */}
      <Section title="Inscriptions et activations (12 derniers mois)">
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 10, height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series_mois}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="mois" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="inscriptions" name="Inscriptions" fill={C.navy} radius={[4, 4, 0, 0]} />
              <Bar dataKey="activations" name="Activations" fill={C.gold} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Répartitions */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          ["Par statut", data.by_statut],
          ["Par rôle", data.by_role],
          ["Par formule", data.by_formule],
          ["Par zone", data.by_zone],
        ].map(([title, obj]) => (
          <div key={title} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>{title}</div>
            {Object.entries(obj).filter(([_, v]) => v > 0).map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: `.5px solid ${C.border}` }}>
                <span>{{ en_integration: "En intégration", actif: "Actif", suspendu: "Suspendu", conseiller: "Consultant", partenaire_recruteur: "Partenaire recruteur" }[k] || k.replace("Conseiller", "Consultant")}</span><strong style={{ color: C.navy }}>{v}</strong>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Top recruteurs */}
      <Section title="Top 10 recruteurs">
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr>{["Recruteur", "Recrues inscrites", "Recrues activées", "Équipe (3 niveaux)"].map(h => (
              <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `.5px solid ${C.border}`, background: C.gray2 }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {data.recruteurs_top.map(r => (
                <tr key={r.id} style={{ borderBottom: `.5px solid ${C.border}` }}>
                  <td style={{ padding: "10px 14px" }}>{r.prenom} {r.nom}</td>
                  <td style={{ padding: "10px 14px", fontWeight: 600 }}>{r.recrues_inscrites}</td>
                  <td style={{ padding: "10px 14px", color: C.green, fontWeight: 600 }}>{r.recrues_activees}</td>
                  <td style={{ padding: "10px 14px", color: C.navy, fontWeight: 600 }}>{r.equipe_totale}</td>
                </tr>
              ))}
              {!data.recruteurs_top.length && <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 12 }}>Aucun recruteur avec une équipe</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* À traiter */}
      <Section title={`À traiter (${data.a_traiter.length})`}>
        {!data.a_traiter.length ? (
          <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 20 }}>Tout est à jour 🎉</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {data.a_traiter.map((a, i) => (
              <div key={i} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span><strong>{a.type}</strong> : {a.nom}</span>
                <span style={{ color: a.jours > 2 ? C.red : C.mid }}>{a.jours} jour{a.jours > 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// FICHE RECRUE (parcours intégration — affichée si !access_actif)
// ═══════════════════════════════════════════════════════════════════════════
export const MonIntegration = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/mon-integration`, authHdrs()).then(r => setData(r.data)).catch(console.error);
  }, []);
  if (!data) return <Spinner />;
  const c = data.conseiller;
  const pct = Math.round(data.done_count / data.total * 100);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }} data-testid="mon-integration">
      <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`, color: "#fff", padding: 24, borderRadius: 14, marginBottom: 20 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 500 }}>Bienvenue, {c.prenom} 🎉</div>
        <div style={{ fontSize: 13, marginTop: 6, color: "rgba(255,255,255,.8)" }}>Votre parcours d'intégration AUPRÉA · Progression {pct}%</div>
      </div>

      {!c.access_actif && !c.cotisation_payee && (
        <div style={{ background: "#FEF3C7", border: `1px solid #FBBF24`, borderRadius: 10, padding: 16, marginBottom: 20 }} data-testid="cotisation-alert">
          <div style={{ fontSize: 14, fontWeight: 700, color: "#92400E", marginBottom: 8 }}>⚠ Cotisation à régler</div>
          <div style={{ fontSize: 12, color: "#78350F", marginBottom: 12 }}>
            Votre accès au réseau sera automatiquement ouvert dès paiement de votre cotisation Formule <strong>{(c.formule_cotisation || "").replace("Conseiller", "Consultant")}</strong>.
          </div>
          <Btn variant="gold" onClick={() => window.location.href = `/cotisation/${c.id}`} data-testid="btn-payer-cotisation">
            Payer ma cotisation ({fmtEur(data.cotisation_amount)}/mois)
          </Btn>
        </div>
      )}

      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 12 }}>Parcours d'intégration</div>
        <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.golddm})`, transition: "width .4s" }} data-testid="progress-bar" />
        </div>
        {data.steps.map((s, i) => (
          <div key={s.etape} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < data.steps.length - 1 ? `.5px solid ${C.border}` : "none" }} data-testid={`step-${s.etape}`}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: s.done ? C.green : C.gray2, color: s.done ? "#fff" : C.mid, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {s.done ? "✓" : i + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: s.done ? C.text : C.mid }}>{s.label}</div>
              {s.at && <div style={{ fontSize: 10, color: C.muted }}>Validée le {s.at.slice(0, 10)}</div>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 20, textAlign: "center", color: C.mid, fontSize: 11 }}>
        Un question ? Contactez-nous à <a href="mailto:contact@auprea.com" style={{ color: C.gold }}>contact@auprea.com</a>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Page publique de candidature /rejoindre/{code}
// ═══════════════════════════════════════════════════════════════════════════
export const RejoindrePage = ({ code }) => {
  const [info, setInfo] = useState(null);
  const [constants, setConstants] = useState(null);
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", telephone: "", date_naissance: "",
    zone: "Hexagone", situation: "salarie", experience: "",
    formule_cotisation: "Conseiller", rgpd_accept: false,
  });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/invitation/${code}`).then(r => r.data),
      axios.get(`${API}/reseau/constants`).then(r => r.data),
    ]).then(([inv, cst]) => {
      setInfo(inv); setConstants(cst);
      if (inv.prefill) setForm(f => ({ ...f, ...inv.prefill }));
      setLoading(false);
    });
  }, [code]);

  const submit = async () => {
    setErr("");
    if (!form.rgpd_accept) { setErr("Vous devez accepter la politique de confidentialité."); return; }
    try {
      await axios.post(`${API}/public/candidature`, { ...form, parrain_code: code });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <Spinner />;

  if (sent) return (
    <div style={{ maxWidth: 540, margin: "40px auto", background: "#fff", padding: 30, borderRadius: 14, border: `.5px solid ${C.border}`, textAlign: "center" }} data-testid="candidature-sent">
      <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: C.navy }}>Candidature reçue</div>
      <div style={{ fontSize: 13, color: C.mid, marginTop: 10, lineHeight: 1.6 }}>
        Merci {form.prenom} ! Votre candidature a bien été transmise à AUPRÉA. Nous reviendrons vers vous sous 48 h.
      </div>
    </div>
  );

  if (info?.expired) return (
    <div style={{ maxWidth: 540, margin: "40px auto", background: "#fff", padding: 30, borderRadius: 14, border: `1px solid ${C.red}`, textAlign: "center" }}>
      <div style={{ fontSize: 20, color: C.red, fontWeight: 700, marginBottom: 10 }}>⚠ Lien expiré</div>
      <div style={{ fontSize: 13, color: C.mid }}>Ce lien d'invitation n'est plus valable. Demandez-en un nouveau à votre parrain.</div>
    </div>
  );

  return (
    <div style={{ background: `linear-gradient(180deg, ${C.navylt} 0%, #fff 300px)`, minHeight: "100vh", padding: "40px 20px" }}>
      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 40, color: C.navy, fontWeight: 500 }}>AUPRÉA</div>
          <div style={{ fontSize: 14, color: C.gold, letterSpacing: ".2em", textTransform: "uppercase", marginTop: 2 }}>Réseau de consultants</div>
        </div>

        <div style={{ background: "#fff", padding: 30, borderRadius: 14, border: `.5px solid ${C.border}` }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: C.navy, marginBottom: 6 }}>Rejoindre le réseau</div>
          {info?.parrain_nom && (
            <div style={{ background: C.goldlt, padding: 10, borderRadius: 6, fontSize: 13, color: C.golddm, marginBottom: 20 }} data-testid="parrain-info">
              Vous êtes invité(e) par <strong>{info.parrain_nom}</strong>
            </div>
          )}

          {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Input placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="c-prenom" />
              <Input placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="c-nom" />
            </div>
            <Input placeholder="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="c-email" />
            <Input placeholder="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="c-tel" />
            <Input placeholder="Date de naissance" type="date" value={form.date_naissance} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} data-testid="c-dnaiss" />
            <Select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="c-zone">
              {constants.zones.map(z => <option key={z} value={z}>{z}</option>)}
            </Select>
            <Select value={form.situation} onChange={(e) => setForm({ ...form, situation: e.target.value })} data-testid="c-situation">
              <option value="salarie">Salarié(e)</option>
              <option value="independant">Indépendant(e)</option>
              <option value="sans_activite">Sans activité</option>
              <option value="retraite">Retraité(e)</option>
              <option value="autre">Autre</option>
            </Select>
            <Textarea placeholder="Expérience en patrimoine, finance ou vente (3-4 lignes)" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} rows={3} data-testid="c-exp" />
            <Select value={form.formule_cotisation} onChange={(e) => setForm({ ...form, formule_cotisation: e.target.value })} data-testid="c-formule">
              {constants.formules.map(f => <option key={f} value={f}>Formule {f.replace("Conseiller", "Consultant")}</option>)}
            </Select>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12, color: C.mid, lineHeight: 1.5, cursor: "pointer" }}>
              <input type="checkbox" checked={form.rgpd_accept} onChange={(e) => setForm({ ...form, rgpd_accept: e.target.checked })}
                     data-testid="c-rgpd" style={{ marginTop: 3, accentColor: C.gold, width: 16, height: 16 }} />
              J'accepte que mes données soient traitées par AUPRÉA aux fins d'étude de ma candidature. Je peux exercer mes droits RGPD à tout moment via contact@auprea.com.
            </label>
            <Btn variant="gold" onClick={submit} data-testid="c-submit">Envoyer ma candidature</Btn>
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: 11, color: C.mid, marginTop: 20 }}>
          AUPRÉA SAS · SIRET 933 283 459 00016 · <a href="/" style={{ color: C.gold }}>www.auprea.com</a>
        </div>
      </div>
    </div>
  );
};

// ── Helpers UI ──────────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>{title}</div>
    {children}
  </div>
);

const ModalShell = ({ title, onClose, children }) => (
  <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 540, width: "100%", maxHeight: "90vh", overflow: "auto", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{title}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: C.mid, cursor: "pointer" }}>×</button>
      </div>
      {children}
    </div>
  </div>
);
